// SPDX-License-Identifier: GPL-3.0-or-later
// Validation of a TableSpec (JSON from disk or from the editor).
//
// The check has two steps. Step 1 checks the shape of every field against a schema
// (unknown fields are errors, so a typo is not silent). Step 2 checks the links between
// items (names, materials, walls) and the limits of the playfield.
// The editor and the tests import this file. It has no DOM and no three import.

import { CONTRACT_VERSION, type TableSpec } from "../shared/contracts.ts";
import { KNOWN_MESH_IDS } from "./ids.ts";

/** Result of `validateTable`. */
export type ValidationResult =
  | { readonly ok: true; readonly table: TableSpec }
  | { readonly ok: false; readonly errors: readonly string[] };

/** Options of `validateTable`. */
export interface ValidateOptions {
  /** Decoration registry ids that the client can build. Default: KNOWN_MESH_IDS. */
  readonly knownMeshIds?: readonly string[];
  /** Item or light names that must exist (the rules need them). */
  readonly requireNames?: readonly string[];
}

/** Name pattern of an item (contracts: ItemBaseSpec.name). */
export const ITEM_NAME_PATTERN = /^[A-Za-z][A-Za-z0-9_]{0,31}$/;

const MAX_ERRORS = 60;

// ───────────────────────────── schema ─────────────────────────────

type Kind =
  | "num"
  | "int"
  | "bool"
  | "str"
  | "vec2"
  | "vec3"
  | "points"
  | "strs"
  | "name"
  | "collider"
  | "enum";

interface Def {
  readonly kind: Kind;
  readonly req?: boolean;
  readonly min?: number;
  readonly max?: number;
  /** Strictly above zero. */
  readonly pos?: boolean;
  readonly values?: readonly (string | number)[];
}

type Schema = Readonly<Record<string, Def>>;

const num = (o: Partial<Def> = {}): Def => ({ kind: "num", ...o });
const int = (o: Partial<Def> = {}): Def => ({ kind: "int", ...o });
const bool: Def = { kind: "bool" };
const str: Def = { kind: "str" };
const vec2 = (req = true): Def => ({ kind: "vec2", req });
const vec3 = (req = true): Def => ({ kind: "vec3", req });
const points = (req = true): Def => ({ kind: "points", req });
const oneOf = (values: readonly (string | number)[], req = false): Def => ({
  kind: "enum",
  values,
  req,
});
const reqNum = (o: Partial<Def> = {}): Def => ({ kind: "num", req: true, ...o });

const ROLES = [
  "drain",
  "lock",
  "scoop",
  "outlane",
  "inlane",
  "shooter_lane",
  "rollover",
  "none",
] as const;
const COLOR: Partial<Def> = { min: 0, max: 0xffffff };

const BASE: Schema = {
  name: { kind: "name", req: true },
  szSurface: str,
  szMaterial: str,
  isVisible: bool,
  tags: { kind: "strs" },
};

const PHYSICS: Schema = {
  elasticity: num(),
  elasticityFalloff: num(),
  friction: num(),
  scatter: num(),
  threshold: num(),
};

const MATERIAL_SCHEMA: Schema = {
  name: { kind: "name", req: true },
  baseColor: int({ req: true, ...COLOR }),
  roughness: num({ min: 0, max: 1 }),
  glossiness: num({ min: 0, max: 1 }),
  clearCoat: num({ min: 0, max: 1 }),
  isMetal: bool,
  opacity: num({ min: 0, max: 1 }),
  isOpacityActive: bool,
  emissiveColor: int(COLOR),
  emissiveIntensity: num({ min: 0 }),
  elasticity: num(),
  elasticityFalloff: num(),
  friction: num(),
  scatterAngle: num(),
};

const FLIPPER_SCHEMA: Schema = {
  ...BASE,
  ...PHYSICS,
  center: vec2(),
  baseRadius: reqNum({ pos: true }),
  endRadius: reqNum({ pos: true }),
  flipperRadiusMax: reqNum({ pos: true }),
  startAngle: reqNum(),
  endAngle: reqNum(),
  height: reqNum({ pos: true }),
  mass: num({ pos: true }),
  strength: num({ pos: true }),
  return: num({ min: 0 }),
  rampUp: num({ min: 0 }),
  torqueDamping: num({ min: 0 }),
  torqueDampingAngle: num({ min: 0 }),
  rubberThickness: num({ min: 0 }),
  rubberHeight: num({ min: 0 }),
  rubberWidth: num({ min: 0 }),
  szRubberMaterial: str,
  side: oneOf(["left", "right"], true),
  isEnabled: bool,
};

const BUMPER_SCHEMA: Schema = {
  ...BASE,
  center: vec2(),
  radius: reqNum({ pos: true }),
  force: reqNum({ min: 0 }),
  threshold: num({ min: 0 }),
  scatter: num(),
  heightScale: num({ pos: true }),
  orientation: num(),
  ringSpeed: num({ min: 0 }),
  szCapMaterial: str,
  szRingMaterial: str,
  szBaseMaterial: str,
  szSkirtMaterial: str,
  isCollidable: bool,
  capDecoration: str,
};

const WALL_SCHEMA: Schema = {
  ...BASE,
  ...PHYSICS,
  dragPoints: points(),
  heightBottom: num({ min: 0 }),
  heightTop: reqNum({ pos: true }),
  hitEvent: bool,
  isDroppable: bool,
  isCollidable: bool,
  isBottomSolid: bool,
  slingshotForce: num({ min: 0 }),
  slingshotThreshold: num({ min: 0 }),
  szTopMaterial: str,
  szSideMaterial: str,
  szSlingShotMaterial: str,
  isTopBottomVisible: bool,
  isSideVisible: bool,
};

const RUBBER_SCHEMA: Schema = {
  ...BASE,
  ...PHYSICS,
  dragPoints: points(),
  height: reqNum({ min: 0 }),
  hitHeight: num(),
  thickness: reqNum({ pos: true }),
  hitEvent: bool,
  isCollidable: bool,
  rotX: num(),
  rotY: num(),
  rotZ: num(),
};

const RAMP_SCHEMA: Schema = {
  ...BASE,
  ...PHYSICS,
  dragPoints: points(),
  heightBottom: reqNum({ min: 0 }),
  heightTop: reqNum({ min: 0 }),
  widthBottom: reqNum({ pos: true }),
  widthTop: reqNum({ pos: true }),
  rampType: oneOf([0, 1, 2, 3, 4, 5]),
  leftWallHeight: num({ min: 0 }),
  rightWallHeight: num({ min: 0 }),
  leftWallHeightVisible: num({ min: 0 }),
  rightWallHeightVisible: num({ min: 0 }),
  wireDiameter: num({ pos: true }),
  wireDistanceX: num({ min: 0 }),
  wireDistanceY: num({ min: 0 }),
  hitEvent: bool,
  isCollidable: bool,
};

const KICKER_SCHEMA: Schema = {
  ...BASE,
  center: vec2(),
  radius: reqNum({ pos: true }),
  kickerType: oneOf([0, 1, 2, 3, 6]),
  orientation: num(),
  hitAccuracy: num({ min: 0 }),
  hitHeight: num({ min: 0 }),
  scatter: num(),
  fallThrough: bool,
  isEnabled: bool,
  role: oneOf(ROLES),
  ejectAngle: num(),
  ejectSpeed: num({ min: 0 }),
};

const TRIGGER_SCHEMA: Schema = {
  ...BASE,
  center: vec2(),
  dragPoints: points(false),
  radius: num({ pos: true }),
  rotation: num(),
  shape: oneOf([0, 1, 2, 3, 4, 5, 6]),
  hitHeight: num({ min: 0 }),
  wireThickness: num({ min: 0 }),
  isEnabled: bool,
  role: oneOf(ROLES),
};

const GATE_SCHEMA: Schema = {
  ...BASE,
  center: vec2(),
  length: reqNum({ pos: true }),
  height: reqNum({ min: 0 }),
  rotation: reqNum(),
  angleMin: num(),
  angleMax: num(),
  damping: num({ min: 0 }),
  elasticity: num(),
  friction: num(),
  gravityFactor: num(),
  gateType: oneOf([1, 2, 3, 4]),
  twoWay: bool,
  showBracket: bool,
  isCollidable: bool,
};

const SPINNER_SCHEMA: Schema = {
  ...BASE,
  center: vec2(),
  length: reqNum({ pos: true }),
  height: reqNum({ min: 0 }),
  rotation: reqNum(),
  damping: num({ min: 0 }),
  elasticity: num(),
  angleMin: num(),
  angleMax: num(),
  showBracket: bool,
};

const PLUNGER_SCHEMA: Schema = {
  ...BASE,
  center: vec2(),
  width: reqNum({ pos: true }),
  height: reqNum({ pos: true }),
  zAdjust: num(),
  stroke: num({ pos: true }),
  speedPull: num({ pos: true }),
  speedFire: num({ pos: true }),
  mechStrength: num({ pos: true }),
  parkPosition: num({ min: 0, max: 1 }),
  scatterVelocity: num({ min: 0 }),
  momentumXfer: num({ min: 0 }),
  autoPlunger: bool,
};

const LIGHT_SCHEMA: Schema = {
  ...BASE,
  center: vec2(),
  dragPoints: points(false),
  falloff: num({ pos: true }),
  falloffPower: num({ min: 0 }),
  color: int({ req: true, ...COLOR }),
  color2: int(COLOR),
  intensity: num({ min: 0 }),
  state: oneOf([0, 1, 2]),
  rgBlinkPattern: str,
  blinkInterval: num({ pos: true }),
  bulbLight: bool,
  showBulbMesh: bool,
  meshRadius: num({ pos: true }),
  bulbHaloHeight: num({ min: 0 }),
  label: str,
};

const TARGET_SCHEMA: Schema = {
  ...BASE,
  ...PHYSICS,
  position: vec3(),
  vSize: vec3(false),
  rotZ: num(),
  targetType: oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9], true),
  isDropped: bool,
  dropSpeed: num({ pos: true }),
  raiseDelay: num({ min: 0 }),
  isCollidable: bool,
  label: str,
};

const DECORATION_SCHEMA: Schema = {
  ...BASE,
  meshId: str,
  position: vec3(),
  rotation: vec3(false),
  scale: vec3(false),
  collider: { kind: "collider" },
  animations: { kind: "strs" },
};

const INFO_SCHEMA: Schema = {
  name: { kind: "str", req: true },
  left: reqNum(),
  top: reqNum(),
  right: reqNum(),
  bottom: reqNum(),
  glassHeight: reqNum({ pos: true }),
  tableHeight: num(),
  angletiltMin: reqNum({ min: 0, max: 30 }),
  angleTiltMax: reqNum({ min: 0, max: 30 }),
  globalDifficulty: reqNum({ min: 0, max: 1 }),
  gravity: reqNum({ pos: true }),
  friction: reqNum({ min: 0, max: 1 }),
  elasticity: reqNum({ min: 0, max: 1 }),
  elasticityFalloff: reqNum({ min: 0 }),
  scatter: reqNum({ min: 0 }),
  defaultScatter: num({ min: 0 }),
  plungerNormalize: num({ min: 0 }),
  plungerFilter: bool,
  szPlayfieldMaterial: str,
  playfieldArt: str,
  ballRadius: reqNum({ pos: true }),
  ballMass: reqNum({ pos: true }),
};

/** Fixed item arrays of a TableSpec, with the schema of each. */
const COLLECTIONS: readonly (readonly [string, Schema])[] = [
  ["flippers", FLIPPER_SCHEMA],
  ["bumpers", BUMPER_SCHEMA],
  ["walls", WALL_SCHEMA],
  ["rubbers", RUBBER_SCHEMA],
  ["ramps", RAMP_SCHEMA],
  ["kickers", KICKER_SCHEMA],
  ["triggers", TRIGGER_SCHEMA],
  ["gates", GATE_SCHEMA],
  ["spinners", SPINNER_SCHEMA],
  ["lights", LIGHT_SCHEMA],
  ["targets", TARGET_SCHEMA],
  ["decorations", DECORATION_SCHEMA],
];

/** One field of an item that the editor can show and set. */
export interface FieldDef {
  readonly key: string;
  /** "vec2" has the axes x and y. "vec3" has x, y and z. */
  readonly type: "number" | "boolean" | "string" | "vec2" | "vec3";
  /** The allowed values of an enum field. */
  readonly values?: readonly (string | number)[];
  readonly required: boolean;
}

const EDITABLE: Partial<Record<Kind, FieldDef["type"]>> = {
  num: "number",
  int: "number",
  enum: "number",
  bool: "boolean",
  str: "string",
  vec2: "vec2",
  vec3: "vec3",
};

/**
 * List every scalar and vector field that the schema of an item list allows.
 * `listKey` is "plunger" or the name of a list ("flippers", "walls", ...).
 * Names, drag points, tag lists and colliders are not listed.
 * The result is empty for an unknown key.
 */
export function itemFieldDefs(listKey: string): readonly FieldDef[] {
  const schema =
    listKey === "plunger" ? PLUNGER_SCHEMA : COLLECTIONS.find(([k]) => k === listKey)?.[1];
  if (!schema) return [];
  const out: FieldDef[] = [];
  for (const [key, def] of Object.entries(schema)) {
    const field = editableField(key, def);
    if (field) out.push(field);
  }
  return out;
}

/** The editor field of a schema entry. `null` for a name and for a kind that the editor skips. */
function editableField(key: string, def: Def): FieldDef | null {
  const type = EDITABLE[def.kind];
  if (!type || key === "name") return null;
  const enumType = def.kind === "enum" && typeof def.values?.[0] === "string" ? "string" : type;
  return {
    key,
    type: enumType,
    ...(def.values ? { values: def.values } : {}),
    required: def.req === true,
  };
}

const TOP_KEYS = new Set([
  "version",
  "id",
  "info",
  "materials",
  "plunger",
  "ballStart",
  ...COLLECTIONS.map(([k]) => k),
]);

// ───────────────────────────── step 1: shape ─────────────────────────────

type Obj = Record<string, unknown>;

function isObj(v: unknown): v is Obj {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

class Errors {
  readonly list: string[] = [];
  add(path: string, msg: string): void {
    if (this.list.length < MAX_ERRORS) this.list.push(`${path}: ${msg}`);
  }
  get count(): number {
    return this.list.length;
  }
}

function checkNumber(v: unknown, def: Def, path: string, errs: Errors): void {
  if (typeof v !== "number" || !Number.isFinite(v)) {
    errs.add(path, "must be a finite number");
    return;
  }
  if (def.kind === "int" && !Number.isInteger(v)) errs.add(path, "must be a whole number");
  if (def.pos && v <= 0) errs.add(path, "must be above 0");
  checkBounds(v, def, path, errs);
}

/** Check the min and max of a number field. */
function checkBounds(v: number, def: Def, path: string, errs: Errors): void {
  if (def.min !== undefined && v < def.min) errs.add(path, `must be ${def.min} or more`);
  if (def.max !== undefined && v > def.max) errs.add(path, `must be ${def.max} or less`);
}

function checkVec(v: unknown, dims: 2 | 3, path: string, errs: Errors): void {
  if (!isObj(v)) {
    errs.add(path, "must be an object");
    return;
  }
  const keys = dims === 2 ? ["x", "y"] : ["x", "y", "z"];
  for (const k of keys) {
    if (typeof v[k] !== "number" || !Number.isFinite(v[k]))
      errs.add(`${path}.${k}`, "must be a finite number");
  }
  for (const k of Object.keys(v)) if (!keys.includes(k)) errs.add(`${path}.${k}`, "unknown field");
}

const DRAG_KEYS = new Set(["vertex", "fSmooth", "fSlingshot", "fAutoTexture", "texturecoord"]);

function checkPoints(v: unknown, path: string, errs: Errors): void {
  if (!Array.isArray(v)) {
    errs.add(path, "must be an array");
    return;
  }
  v.forEach((p: unknown, i) => {
    checkPoint(p, `${path}[${i}]`, errs);
  });
}

/** Check one drag point. */
function checkPoint(p: unknown, at: string, errs: Errors): void {
  if (!isObj(p)) {
    errs.add(at, "must be an object");
    return;
  }
  checkVec(p.vertex, 3, `${at}.vertex`, errs);
  for (const k of ["fSmooth", "fSlingshot", "fAutoTexture"]) {
    if (p[k] !== undefined && typeof p[k] !== "boolean")
      errs.add(`${at}.${k}`, "must be true or false");
  }
  if (
    p.texturecoord !== undefined &&
    (typeof p.texturecoord !== "number" || !Number.isFinite(p.texturecoord))
  ) {
    errs.add(`${at}.texturecoord`, "must be a finite number");
  }
  for (const k of Object.keys(p)) if (!DRAG_KEYS.has(k)) errs.add(`${at}.${k}`, "unknown field");
}

type FieldCheck = (v: unknown, def: Def, path: string, errs: Errors) => void;

function checkBool(v: unknown, _def: Def, path: string, errs: Errors): void {
  if (typeof v !== "boolean") errs.add(path, "must be true or false");
}

function checkStr(v: unknown, _def: Def, path: string, errs: Errors): void {
  if (typeof v !== "string" || v.length === 0 || v.length > 200)
    errs.add(path, "must be a text of 1 to 200 characters");
}

function checkName(v: unknown, _def: Def, path: string, errs: Errors): void {
  if (typeof v !== "string" || !ITEM_NAME_PATTERN.test(v))
    errs.add(path, "must match [A-Za-z][A-Za-z0-9_]{0,31}");
}

function checkStrs(v: unknown, _def: Def, path: string, errs: Errors): void {
  if (
    !Array.isArray(v) ||
    v.some((s) => typeof s !== "string" || s.length === 0 || s.length > 64)
  ) {
    errs.add(path, "must be an array of texts (1 to 64 characters)");
  }
}

function checkEnum(v: unknown, def: Def, path: string, errs: Errors): void {
  if (!def.values?.includes(v as string | number))
    errs.add(path, `must be one of ${JSON.stringify(def.values)}`);
}

function checkCollider(v: unknown, _def: Def, path: string, errs: Errors): void {
  if (!isObj(v) || v.kind !== "cylinder") {
    errs.add(path, 'must be { kind: "cylinder", radius, height }');
    return;
  }
  for (const k of ["radius", "height"])
    checkNumber(v[k], { kind: "num", pos: true }, `${path}.${k}`, errs);
  for (const k of Object.keys(v))
    if (!["kind", "radius", "height"].includes(k)) errs.add(`${path}.${k}`, "unknown field");
}

/** The check of each kind of field. */
const FIELD_CHECKS: Readonly<Record<Kind, FieldCheck>> = {
  num: checkNumber,
  int: checkNumber,
  bool: checkBool,
  str: checkStr,
  name: checkName,
  vec2: (v, _def, path, errs) => checkVec(v, 2, path, errs),
  vec3: (v, _def, path, errs) => checkVec(v, 3, path, errs),
  points: (v, _def, path, errs) => checkPoints(v, path, errs),
  strs: checkStrs,
  enum: checkEnum,
  collider: checkCollider,
};

function checkField(v: unknown, def: Def, path: string, errs: Errors): void {
  FIELD_CHECKS[def.kind](v, def, path, errs);
}

function checkObject(v: unknown, schema: Schema, path: string, errs: Errors): void {
  if (!isObj(v)) {
    errs.add(path, "must be an object");
    return;
  }
  for (const key of Object.keys(v))
    if (!(key in schema)) errs.add(`${path}.${key}`, "unknown field");
  for (const [key, def] of Object.entries(schema)) {
    const value = v[key];
    if (value === undefined) {
      if (def.req) errs.add(`${path}.${key}`, "is required");
      continue;
    }
    checkField(value, def, `${path}.${key}`, errs);
  }
}

// ───────────────────────────── step 2: links and limits ─────────────────────────────

interface Bounds {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

interface Pt {
  x: number;
  y: number;
}

function inside(b: Bounds, p: Pt): boolean {
  return p.x >= b.left && p.x <= b.right && p.y >= b.top && p.y <= b.bottom;
}

function pointsOf(item: Obj): Pt[] {
  const raw = item.dragPoints;
  if (!Array.isArray(raw)) return [];
  const out: Pt[] = [];
  for (const dp of raw) {
    if (isObj(dp) && isObj(dp.vertex)) out.push({ x: Number(dp.vertex.x), y: Number(dp.vertex.y) });
  }
  return out;
}

const MATERIAL_FIELDS = [
  "szMaterial",
  "szTopMaterial",
  "szSideMaterial",
  "szSlingShotMaterial",
  "szCapMaterial",
  "szRingMaterial",
  "szBaseMaterial",
  "szSkirtMaterial",
  "szRubberMaterial",
];

/** The names and limits that the link checks share. */
interface LinkState {
  readonly t: Obj;
  readonly errs: Errors;
  readonly bounds: Bounds;
  readonly materialNames: ReadonlySet<string>;
  readonly wallNames: ReadonlySet<string>;
  readonly itemNames: Set<string>;
  readonly known: ReadonlySet<string>;
}

/** Check the playfield box and the tilt angles. Return the box. */
function checkInfoBounds(info: Obj, errs: Errors): Bounds {
  const bounds: Bounds = {
    left: info.left as number,
    top: info.top as number,
    right: info.right as number,
    bottom: info.bottom as number,
  };
  if (bounds.left >= bounds.right) errs.add("info", "left must be less than right");
  if (bounds.top >= bounds.bottom) errs.add("info", "top must be less than bottom");
  if ((info.angletiltMin as number) > (info.angleTiltMax as number))
    errs.add("info", "angletiltMin must not exceed angleTiltMax");
  return bounds;
}

/** Collect the material names. A repeated name is an error. */
function collectMaterialNames(t: Obj, errs: Errors): Set<string> {
  const materialNames = new Set<string>();
  (t.materials as Obj[]).forEach((m, i) => {
    const name = m.name as string;
    if (materialNames.has(name))
      errs.add(`materials[${i}].name`, `duplicate material name "${name}"`);
    materialNames.add(name);
  });
  return materialNames;
}

function claimName(s: LinkState, path: string, name: string): void {
  if (s.itemNames.has(name)) s.errs.add(`${path}.name`, `duplicate item name "${name}"`);
  s.itemNames.add(name);
}

/** Check that the materials and the wall of an item exist. */
function checkRefs(s: LinkState, path: string, item: Obj): void {
  for (const f of MATERIAL_FIELDS) {
    const v = item[f];
    if (typeof v === "string" && !s.materialNames.has(v))
      s.errs.add(`${path}.${f}`, `unknown material "${v}"`);
  }
  if (typeof item.szSurface === "string" && !s.wallNames.has(item.szSurface)) {
    s.errs.add(`${path}.szSurface`, `unknown wall "${item.szSurface}"`);
  }
}

function checkWithin(s: LinkState, path: string, p: Pt): void {
  if (!inside(s.bounds, p)) s.errs.add(path, `point (${p.x}, ${p.y}) is outside the playfield`);
}

/** Check the name, the links and the position of one item of a list. */
function checkItemLinks(s: LinkState, path: string, item: Obj): void {
  claimName(s, path, item.name as string);
  checkRefs(s, path, item);
  if (isObj(item.center)) checkWithin(s, `${path}.center`, item.center as unknown as Pt);
  if (isObj(item.position)) checkWithin(s, `${path}.position`, item.position as unknown as Pt);
  pointsOf(item).forEach((p, j) => {
    checkWithin(s, `${path}.dragPoints[${j}]`, p);
  });
}

/** Check the names, the links and the positions of every item and of the plunger and the ball start. */
function checkItems(s: LinkState): void {
  for (const [key] of COLLECTIONS) {
    (s.t[key] as Obj[]).forEach((item, i) => {
      checkItemLinks(s, `${key}[${i}]`, item);
    });
  }
  const plunger = s.t.plunger as Obj;
  claimName(s, "plunger", plunger.name as string);
  checkRefs(s, "plunger", plunger);
  checkWithin(s, "plunger.center", plunger.center as unknown as Pt);
  checkWithin(s, "ballStart", s.t.ballStart as unknown as Pt);
}

/** Check the counts of drag points of walls, rubbers, ramps, triggers and lights. */
function checkPointCounts(t: Obj, errs: Errors): void {
  (t.walls as Obj[]).forEach((w, i) => {
    if (pointsOf(w).length < 3) errs.add(`walls[${i}].dragPoints`, "a wall needs 3 or more points");
    const bottom = typeof w.heightBottom === "number" ? w.heightBottom : 0;
    if ((w.heightTop as number) <= bottom)
      errs.add(`walls[${i}].heightTop`, "must be above heightBottom");
  });
  for (const key of ["rubbers", "ramps"]) {
    (t[key] as Obj[]).forEach((r, i) => {
      if (pointsOf(r).length < 2) errs.add(`${key}[${i}].dragPoints`, "needs 2 or more points");
    });
  }
  for (const key of ["triggers", "lights"]) {
    (t[key] as Obj[]).forEach((r, i) => {
      checkOutline(key, r, i, errs);
    });
  }
}

/** A trigger or a light has an outline of 3 or more points, or no outline and a radius or falloff. */
function checkOutline(key: string, r: Obj, i: number, errs: Errors): void {
  const n = pointsOf(r).length;
  if (n > 0 && n < 3)
    errs.add(`${key}[${i}].dragPoints`, "an outline needs 3 or more points (or leave it empty)");
  if (n === 0 && key === "triggers" && r.radius === undefined)
    errs.add(`${key}[${i}].radius`, "is required when there is no outline");
  if (n === 0 && key === "lights" && r.falloff === undefined)
    errs.add(`${key}[${i}].falloff`, "is required when there is no outline");
}

/** Check the flipper angles and that both sides exist. */
function checkFlippers(t: Obj, errs: Errors): void {
  const flippers = t.flippers as Obj[];
  flippers.forEach((f, i) => {
    if (f.startAngle === f.endAngle)
      errs.add(`flippers[${i}]`, "startAngle and endAngle must differ");
  });
  if (!flippers.some((f) => f.side === "left"))
    errs.add("flippers", 'needs one flipper with side "left"');
  if (!flippers.some((f) => f.side === "right"))
    errs.add("flippers", 'needs one flipper with side "right"');
}

/** Check that exactly one kicker is the drain. */
function checkDrain(t: Obj, errs: Errors): void {
  const drains = (t.kickers as Obj[]).filter((k) => k.role === "drain");
  if (drains.length !== 1)
    errs.add("kickers", `needs exactly one kicker with role "drain" (found ${drains.length})`);
}

/** Check the registry ids of the decorations and the bumper caps. */
function checkDecorationIds(s: LinkState): void {
  (s.t.decorations as Obj[]).forEach((d, i) => {
    if (typeof d.meshId !== "string") s.errs.add(`decorations[${i}].meshId`, "is required");
    else if (!s.known.has(d.meshId))
      s.errs.add(`decorations[${i}].meshId`, `unknown registry id "${d.meshId}"`);
  });
  (s.t.bumpers as Obj[]).forEach((b, i) => {
    if (typeof b.capDecoration === "string" && !s.known.has(b.capDecoration)) {
      s.errs.add(`bumpers[${i}].capDecoration`, `unknown registry id "${b.capDecoration}"`);
    }
  });
}

/** Check the playfield art id and the playfield material of the info block. */
function checkInfoRefs(s: LinkState, info: Obj): void {
  if (typeof info.playfieldArt === "string" && !s.known.has(info.playfieldArt)) {
    s.errs.add("info.playfieldArt", `unknown registry id "${info.playfieldArt}"`);
  }
  if (
    typeof info.szPlayfieldMaterial === "string" &&
    !s.materialNames.has(info.szPlayfieldMaterial)
  ) {
    s.errs.add("info.szPlayfieldMaterial", `unknown material "${info.szPlayfieldMaterial}"`);
  }
}

function checkLinks(t: Obj, opts: ValidateOptions, errs: Errors): void {
  const info = t.info as Obj;
  const bounds = checkInfoBounds(info, errs);
  const s: LinkState = {
    t,
    errs,
    bounds,
    materialNames: collectMaterialNames(t, errs),
    wallNames: new Set((t.walls as Obj[]).map((w) => w.name as string)),
    itemNames: new Set<string>(),
    known: new Set(opts.knownMeshIds ?? KNOWN_MESH_IDS),
  };
  checkItems(s);
  checkPointCounts(t, errs);
  checkFlippers(t, errs);
  checkDrain(t, errs);
  checkDecorationIds(s);
  checkInfoRefs(s, info);

  // Lights and items share one name space for the rules.
  for (const required of opts.requireNames ?? []) {
    if (!s.itemNames.has(required))
      errs.add("names", `the rules need an item or light named "${required}"`);
  }
}

// ───────────────────────────── entry point ─────────────────────────────

/** Check the top-level keys, the contract version and the table id. */
function checkHeader(input: Obj, errs: Errors): void {
  for (const key of Object.keys(input))
    if (!TOP_KEYS.has(key)) errs.add(`table.${key}`, "unknown field");
  if (input.version !== CONTRACT_VERSION) errs.add("table.version", `must be ${CONTRACT_VERSION}`);
  if (typeof input.id !== "string" || !/^[a-z][a-z0-9-]{0,31}$/.test(input.id)) {
    errs.add("table.id", "must match [a-z][a-z0-9-]{0,31}");
  }
}

function checkMaterials(materials: unknown, errs: Errors): void {
  if (!Array.isArray(materials)) {
    errs.add("materials", "must be an array");
    return;
  }
  materials.forEach((m: unknown, i) => {
    checkObject(m, MATERIAL_SCHEMA, `materials[${i}]`, errs);
  });
}

/** Check the shape of every item list. */
function checkCollections(input: Obj, errs: Errors): void {
  for (const [key, schema] of COLLECTIONS) {
    const list = input[key];
    if (!Array.isArray(list)) {
      errs.add(key, "must be an array");
      continue;
    }
    list.forEach((item: unknown, i) => {
      checkObject(item, schema, `${key}[${i}]`, errs);
    });
  }
}

/**
 * Check a value that should be a TableSpec.
 * Return the same object (typed) when it is valid, or a list of errors (at most 60).
 */
export function validateTable(input: unknown, opts: ValidateOptions = {}): ValidationResult {
  const errs = new Errors();
  if (!isObj(input)) return { ok: false, errors: ["table: must be an object"] };

  checkHeader(input, errs);
  checkObject(input.info, INFO_SCHEMA, "info", errs);
  checkVec(input.ballStart, 3, "ballStart", errs);
  checkObject(input.plunger, PLUNGER_SCHEMA, "plunger", errs);
  checkMaterials(input.materials, errs);
  checkCollections(input, errs);

  // Step 2 needs a clean shape.
  if (errs.count === 0) checkLinks(input, opts, errs);
  if (errs.count > 0) return { ok: false, errors: errs.list };
  return { ok: true, table: input as unknown as TableSpec };
}
